import {
  AwardIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlusIcon,
  RupeeIcon,
  TextInput,
  useToast,
} from '@razorpay/blade/components';
import useFetchPayDetailsData from 'components/PayDetails/hooks/useFetchPayDetailsData';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import { useForm } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import listPerquisitesComponents from 'api/gql-utils/perquisitesComponents/listPerquisitesComponents';
import { PerquisitesFormData, PerquisiteData } from '../types';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';

const PerquisitesModal = ({
  initialFormData,
  isOpen,
  onDismiss,
  onSubmit,
  perquisitesAssignments,
  isCTALoading = false,
}: {
  initialFormData: PerquisitesFormData;
  isOpen: boolean;
  onDismiss: () => void;
  onSubmit: (formData: PerquisitesFormData) => void;
  perquisitesAssignments: PerquisiteData[];
  isCTALoading?: boolean;
}) => {
  const { formData, setFieldValue, setFormData, setFieldError, errors, clearAllErrors } =
    useForm<PerquisitesFormData>(initialFormData);
  const { urlParams } = useSearchParams();
  const userId = urlParams.get('userId');
  const isEdit = initialFormData.componentId !== '';
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const peopleId = String(userId ?? loggedInUserId);
  const { firstName } = useFetchPayDetailsData(peopleId);
  const toast = useToast();
  useEffect(() => {
    clearAllErrors();
    setFormData(initialFormData);
  }, [initialFormData]);

  const {
    data: options = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [listPerquisitesComponents, { status: 'active' }],
    queryFn: () =>
      listPerquisitesComponents({
        status: 'active',
      }),
    select: (data) => {
      return data?.payrollListPerquisiteComponents?.components || [];
    },
    enabled: isOpen,
    onError: (err) => {
      toast.show({
        color: 'negative',
        content: 'Error fetching perquisite components',
      });
    },
  });

  const onChange = (value: string) => {
    setFieldValue('componentId', value);
  };
  const nonAssignedComponents = useMemo(() => {
    return options.filter(
      (option) =>
        !perquisitesAssignments.some((assignment) => assignment.componentId === option.id) ||
        option.id === initialFormData.componentId,
    );
  }, [options, perquisitesAssignments, initialFormData.componentId]);

  const history = useHistory();
  const openPerquisitesCreation = () => {
    history.push(routePaths.salaryComponents.create.perquisites.root);
  };

  const handleSubmit = () => {
    clearAllErrors();
    let hasErrors = false;

    if (!formData.componentId) {
      setFieldError('componentId', 'Please select a perquisite');
      hasErrors = true;
    }

    if (!formData.totalValue) {
      setFieldError('totalValue', 'Total value is required');
      hasErrors = true;
    }

    if (
      formData?.totalValue &&
      formData?.amountPaidByEmployee &&
      formData?.totalValue < formData?.amountPaidByEmployee
    ) {
      setFieldError('totalValue', 'Total value cannot be less than amount paid by employee');
      hasErrors = true;
    }

    if (!hasErrors) {
      onSubmit(formData);
    }
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="medium">
      <ModalHeader
        title={`${isEdit ? 'Edit' : 'Add'} Perquisites for ${firstName}`}
        subtitle={'Perks that employees enjoy over and above their salary'}
        leading={
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            backgroundColor={'surface.background.sea.subtle'}
            padding={'spacing.3'}
            borderRadius={'round'}
            marginTop={'spacing.4'}>
            <AwardIcon color="surface.icon.onSea.onIntense" />
          </Box>
        }
      />
      <ModalBody padding="spacing.6">
        <Box
          padding={'spacing.8'}
          display="flex"
          flexDirection="column"
          gap="spacing.5"
          backgroundColor={'surface.background.gray.moderate'}
          borderRadius={'xlarge'}>
          <AutoCompleteV2
            label="Perquisites"
            placeholder={
              isLoading
                ? 'Loading components...'
                : isError
                ? 'Error loading components'
                : 'Search for a component'
            }
            options={nonAssignedComponents}
            onChange={(item) => onChange(item.id)}
            value={formData.componentId ?? ''}
            itemToLabel={(item) => item.name}
            itemToKey={(item) => item.id}
            itemToDescription={(item) => item.description}
            necessityIndicator={'required'}
            width={'680px'}
            isDisabled={isLoading || isError}
            isRequired={true}
            validationState={errors?.componentId ? 'error' : 'none'}
            errorText={errors?.componentId}
            footer={
              <Button
                variant="tertiary"
                size="small"
                icon={PlusIcon}
                onClick={openPerquisitesCreation}
                isFullWidth>
                Create perquisites
              </Button>
            }
            accessibilityLabel="add perquisites component"
          />
          <Box display="flex" gap="spacing.5" justifyContent="space-between">
            <Box width={'100%'}>
              <TextInput
                label="Total Value (annual)"
                necessityIndicator={'required'}
                placeholder="Enter amount"
                leadingIcon={RupeeIcon}
                value={formData.totalValue != null ? formData.totalValue.toString() : ''}
                onChange={(e) => {
                  const value = e.value || '';
                  setFieldValue('totalValue', value === '' ? 0 : parseFloat(value));
                }}
                isRequired={true}
                validationState={errors?.totalValue ? 'error' : 'none'}
                errorText={errors?.totalValue}
              />
            </Box>
            <Box width={'100%'}>
              <TextInput
                label="Amount Paid by Employee"
                placeholder="Enter amount"
                leadingIcon={RupeeIcon}
                value={
                  formData.amountPaidByEmployee != null
                    ? formData.amountPaidByEmployee.toString()
                    : ''
                }
                onChange={(e) => {
                  const value = e.value || '';
                  setFieldValue('amountPaidByEmployee', value === '' ? 0 : parseFloat(value));
                }}
                isRequired={true}
                validationState={errors?.amountPaidByEmployee ? 'error' : 'none'}
                errorText={errors?.amountPaidByEmployee}
              />
            </Box>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.5">
          <Button variant="tertiary" onClick={onDismiss}>
            Back
          </Button>
          <Button variant="secondary" onClick={handleSubmit} isLoading={isCTALoading}>
            {isEdit ? 'Modify Perquisite' : 'Add Perquisite'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default PerquisitesModal;
